<template>
  <div class="wb" :style="vars">
    <header class="wb-head">
      <div class="wb-brand">
        <img v-if="logoSrc" :src="logoSrc" alt="" class="wb-logo">
        <span v-else class="wb-monogram" aria-hidden="true">{{ monogram }}</span>
        <span class="wb-brand-name">{{ brand.name }}</span>
        <a v-if="brand.website_url" :href="brand.website_url" class="wb-site" rel="noopener" target="_blank">Visit website<span class="pb-sr-only"> (opens in a new tab)</span></a>
      </div>
      <component :is="preview ? 'h2' : 'h1'" class="wb-title display-font">{{ settings.headline || ('What customers say about ' + brand.name) }}</component>
      <p v-if="settings.intro" class="wb-intro">{{ settings.intro }}</p>
      <p class="wb-disclosure"><i class="mdi mdi-shield-check-outline" aria-hidden="true"></i>{{ disclosure }}</p>
    </header>

    <p v-if="!items.length" class="wb-none">No testimonials are published here yet.</p>

    <div v-else-if="layout === 'grid'" class="wb-grid">
      <TestimonialCard v-for="item in items" :key="item.ref || item.id" :item="item" :show-rating="settings.show_rating !== false" />
    </div>

    <div v-else-if="layout === 'cards'" class="wb-row" tabindex="0" role="region" aria-label="Testimonials, scroll sideways for more">
      <TestimonialCard v-for="item in items" :key="item.ref || item.id" :item="item" :show-rating="settings.show_rating !== false" class="wb-row-card" />
    </div>

    <div v-else :class="['wb-slider', { big: layout === 'quote' }]">
      <div class="wb-slide" aria-live="polite" tabindex="0" role="group" aria-roledescription="carousel" aria-label="Testimonials"
           @keydown.left.prevent="move(-1)" @keydown.right.prevent="move(1)">
        <TestimonialCard :key="index" :item="items[index]" :show-rating="settings.show_rating !== false" />
      </div>
      <div v-if="items.length > 1" class="wb-nav">
        <button v-if="layout === 'carousel'" type="button" class="pb-button outline" aria-label="Previous testimonial" @click="move(-1)">
          <i class="mdi mdi-arrow-left" aria-hidden="true"></i>
        </button>
        <span class="wb-count">{{ index + 1 }} of {{ items.length }}</span>
        <button type="button" class="pb-button outline" :aria-label="layout === 'quote' ? 'Next quote' : 'Next testimonial'" @click="move(1)">
          <span v-if="layout === 'quote'">Next quote</span><i class="mdi mdi-arrow-right" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <footer class="wb-foot">
      <router-link v-if="removalUrl && !preview" :to="removalPath" class="wb-remove">Is this you? Request removal</router-link>
      <span v-else-if="removalUrl" class="wb-remove">Is this you? Request removal</span>
      <a v-if="attribution && attribution.show" :href="attribution.href" class="wb-attr" target="_blank" rel="noopener">Collected with TrustClap</a>
    </footer>
  </div>
</template>

<script>
import TestimonialCard from './TestimonialCard.vue';
import { brandVars, initials } from '../lib/color';
import { imageUrl } from '../publicUrls';

export default {
  name: 'WallBody',
  components: { TestimonialCard },
  props: {
    brand: { type: Object, default: () => ({}) },
    settings: { type: Object, default: () => ({}) },
    layout: { type: String, default: 'grid' },
    items: { type: Array, default: () => [] },
    disclosure: { type: String, default: '' },
    removalUrl: { type: String, default: '' },
    attribution: { type: Object, default: null },
    preview: { type: Boolean, default: false }
  },
  data: () => ({ index: 0 }),
  computed: {
    vars() { return brandVars(this.brand.color); },
    logoSrc() { return this.brand.logo_url || (this.brand.logo ? imageUrl(this.brand.logo) : ''); },
    monogram() { return initials(this.brand.name || ''); },
    removalPath() {
      try { return new URL(this.removalUrl).pathname; } catch (error) { return this.removalUrl; }
    }
  },
  watch: { items() { if (this.index >= this.items.length) this.index = 0; } },
  methods: {
    move(step) { this.index = (this.index + step + this.items.length) % this.items.length; }
  }
};
</script>

<style scoped>
.wb{color:#1F2328}
.wb-head{margin-bottom:28px}
.wb-title,.wb-intro,.wb-disclosure{max-width:760px}
.wb-brand{display:flex;align-items:center;flex-wrap:wrap;gap:10px;margin-bottom:22px}
.wb-logo{width:44px;height:44px;border-radius:12px;object-fit:contain;background:#fff;border:1px solid #E6DED2}
.wb-monogram{display:inline-grid;place-items:center;width:44px;height:44px;border-radius:12px;background:var(--brand,#B4472B);
  color:var(--brand-on,#fff);font:700 16px var(--font-body)}
.wb-brand-name{font:700 16px/1.3 var(--font-body);color:#1F2328}
.wb-site{margin-left:auto;font-size:14px;color:var(--brand-text,#963A22);text-underline-offset:3px;min-height:44px;display:inline-flex;align-items:center}
.wb-title{font:500 40px/1.15 var(--font-display)!important;letter-spacing:-.8px;margin:0;color:#1F2328}
.wb-intro{margin:14px 0 0;font-size:17px;line-height:1.6;color:#4F4A45;white-space:pre-line}
.wb-disclosure{display:flex;gap:8px;align-items:flex-start;margin:16px 0 0;font-size:14px;line-height:1.5;color:#4F4A45}
.wb-disclosure .mdi{color:var(--brand-text,#963A22);font-size:17px;line-height:21px}
.wb-none{padding:40px 0;color:#6B635B}
/* Block-level cards that never split: browsers balance them across the columns (inline cards left a few of them
   stacked in the first column). */
.wb-grid{columns:300px;column-gap:20px}
.wb-grid>*{margin-bottom:20px;display:flex;width:100%;break-inside:avoid}
.wb-row{display:flex;gap:18px;overflow-x:auto;scroll-snap-type:x mandatory;padding:4px 4px 14px}
.wb-row-card{flex:0 0 min(340px,85%);scroll-snap-align:start}
.wb-slider{max-width:720px}
.wb-slider.big ::v-deep .pb-quote-text{font-size:24px;line-height:1.45}
.wb-slider.big ::v-deep .pb-quote{padding:40px 36px 28px}
.wb-nav{display:flex;align-items:center;gap:12px;margin-top:16px}
.wb-count{font-size:14px;color:#4F4A45;font-variant-numeric:tabular-nums}
.wb-foot{display:flex;flex-wrap:wrap;justify-content:space-between;gap:12px;margin-top:32px;padding-top:18px;border-top:1px solid #E6DED2}
.wb-remove{font-size:14px;color:#4F4A45;text-underline-offset:3px;min-height:44px;display:inline-flex;align-items:center}
.wb-attr{font-size:12px;color:#6B635B;min-height:44px;display:inline-flex;align-items:center}
@media(max-width:600px){
  .wb-title{font-size:30px!important;line-height:1.2!important}
  .wb-site{margin-left:0}
  .wb-grid{columns:1}
}
</style>
